/**
 * Stage Instrument Icons — pure, dependency-free mapping from instrument names
 * and crew roles to stage-plot palette icons.
 *
 * Used by the deterministic stage-plot draft generator (stageplotGenerate.ts)
 * to turn a band's REAL musicians/instruments and crew into palette elements.
 * Kept side-effect-free and unit-testable outside React (same pattern as
 * src/lib/aiImport.ts).
 *
 * RULE: every returned `iconId` MUST belong to `PALETTE_ITEMS` (the closed
 * palette in src/shared/types/stageplot.ts). No invented iconId is ever
 * returned — the tests assert this programmatically.
 */

import type { PaletteCategory } from '../shared/types/stageplot';
import { PALETTE_ITEMS } from '../shared/types/stageplot';

/** The closed set of valid palette iconIds (for tests + safety). */
export const VALID_PALETTE_ICON_IDS: ReadonlySet<string> = new Set(
  PALETTE_ITEMS.map((i) => i.iconId),
);

export type MatchKind = 'exact' | 'keyword' | 'none';

export interface InstrumentIconMatch {
  iconId: string | null;
  /** 0..1 — how confident the mapping is (1 = exact, 0 = none). */
  confidence: number;
  matchedBy: MatchKind;
}

export interface RoleIconMatch {
  iconId: string | null;
  category: PaletteCategory | null;
  /** Whether this role physically occupies stage space (gets an element). */
  onStage: boolean;
}

/**
 * A keyword → icon mapping. `keywords` are matched case-insensitively against
 * the instrument name (substring). `iconId` must exist in PALETTE_ITEMS.
 * `ampIconId` is an optional companion amp placed behind the player.
 */
interface InstrumentRule {
  keywords: string[];
  iconId: string;
  ampIconId?: string;
  /** Exact-name matches (case-insensitive, whole string) get confidence 1. */
  exactNames?: string[];
}

/**
 * FR + EN keyword dictionary covering the real instrument names found in the
 * band data (KADEBOSTANY / SAINT STACY / SAMAEL / My Band) plus the generic
 * vocabulary a user would type. Order matters: the FIRST rule whose keyword
 * matches wins, so put the most specific rules first.
 */
const INSTRUMENT_RULES: InstrumentRule[] = [
  // Drums / percussion
  { keywords: ['drum kit', 'drumkit', 'acoustic drums', 'drums', 'drum set', 'batterie'], iconId: 'kick', exactNames: ['drum kit', 'drumkit', 'acoustic drums', 'drums'] },
  { keywords: ['spd'], iconId: 'spd', exactNames: ['spd', 'spd-sx', 'spd-sx pro'] },
  { keywords: ['snare'], iconId: 'snare' },
  { keywords: ['kick', 'bass drum', 'grosse caisse'], iconId: 'kick' },
  { keywords: ['rack tom', 'racktom', 'tom rack'], iconId: 'racktom' },
  { keywords: ['floor tom', 'floortom', 'tom basse'], iconId: 'floortom' },
  { keywords: ['hi-hat', 'hihat', 'charleston', 'charley'], iconId: 'hihat' },
  { keywords: ['ride cymbal', 'ride'], iconId: 'ride' },
  { keywords: ['crash cymbal', 'crash'], iconId: 'crash' },
  { keywords: ['china cymbal', 'china'], iconId: 'china' },
  { keywords: ['splash cymbal', 'splash'], iconId: 'splash' },
  { keywords: ['cymbal'], iconId: 'cymbal' },
  { keywords: ['drum carpet', 'drum mat', 'drummat', 'tapis de batterie'], iconId: 'drum-mat' },
  { keywords: ['drum stool', 'drumstool', 'tabouret'], iconId: 'stool' },
  { keywords: ['drum hardware', 'hardware'], iconId: 'hardware' },
  // Percussion
  { keywords: ['congas', 'conga'], iconId: 'congas' },
  { keywords: ['bongos', 'bongo'], iconId: 'bongos' },
  { keywords: ['timbales', 'timbale'], iconId: 'timbales' },
  { keywords: ['timpani', 'timbales'], iconId: 'timpani' },
  { keywords: ['gong'], iconId: 'gong' },
  { keywords: ['shaker'], iconId: 'shaker' },
  { keywords: ['tambourine', 'tambourin'], iconId: 'tambourine' },
  { keywords: ['triangle'], iconId: 'triangle' },
  { keywords: ['percussion', 'percussions'], iconId: 'congas' },
  // Bass
  { keywords: ['bass guitar', 'bass-guitar', 'bass', 'basse'], iconId: 'bass-guitar', ampIconId: 'bass-amp', exactNames: ['bass'] },
  { keywords: ['upright bass', 'double bass', 'contrebasse'], iconId: 'upright-bass' },
  { keywords: ['bass amp', 'bass-amp', 'bass cab', 'bass-cab'], iconId: 'bass-amp' },
  // Guitar
  { keywords: ['kemper', 'guitar gp-50', 'guitar quad', 'guitar drop', 'guitar vorph', 'guitar', 'guitare', 'electric guitar', 'e-guitar', 'lead guitar', 'rhythm guitar'], iconId: 'e-guitar', ampIconId: 'guitar-amp', exactNames: ['guitar', 'guitar gp-50', 'guitar quad', 'guitar drop', 'guitar vorph'] },
  { keywords: ['acoustic guitar', 'a-guitar', 'guitare acoustique'], iconId: 'a-guitar' },
  { keywords: ['guitar amp', 'guitar-amp', 'guitar cab', 'guitar-cab'], iconId: 'guitar-amp' },
  { keywords: ['guitar stand', 'guitar-stand'], iconId: 'guitar-stand' },
  // Keys / synths
  { keywords: ['mpc', 'sampler', 'échantillonneur'], iconId: 'mpc', exactNames: ['mpc', 'mpc main', 'mpc spare', 'sampler'] },
  { keywords: ['modular synth', 'modular-synth', 'modular'], iconId: 'modular-synth', exactNames: ['modular'] },
  { keywords: ['monotron'], iconId: 'synth-module', exactNames: ['monotron'] },
  { keywords: ['synth', 'synthesizer', 'synthé', 'clavier'], iconId: 'synth', exactNames: ['synth'] },
  { keywords: ['keys', 'keyboard', 'clavier'], iconId: 'stage-piano', exactNames: ['keys'] },
  { keywords: ['stage piano', 'stage-piano', 'piano de scène'], iconId: 'stage-piano' },
  { keywords: ['grand piano', 'grand-piano'], iconId: 'grand-piano' },
  { keywords: ['upright piano', 'upright-piano'], iconId: 'upright-piano' },
  { keywords: ['organ', 'orgue'], iconId: 'organ' },
  { keywords: ['keytar'], iconId: 'keytar' },
  { keywords: ['keyboard stand', 'keystand', 'pied de clavier'], iconId: 'keystand' },
  { keywords: ['midi keyboard', 'midi-keyboard'], iconId: 'midi-keyboard' },
  { keywords: ['drum machine', 'drum-machine', 'boîte à rythmes'], iconId: 'drum-machine' },
  { keywords: ['groovebox', 'groove box'], iconId: 'groovebox' },
  { keywords: ['pad controller', 'pad-controller'], iconId: 'pad-controller' },
  { keywords: ['synth module', 'synth-module'], iconId: 'synth-module' },
  // Vocals
  { keywords: ['lead vocal', 'backing vocal', 'spare vocal', 'vocals', 'vocal', 'chant', 'chanteur', 'chanteuse'], iconId: 'vocal-mic', exactNames: ['vocal', 'vocals', 'lead vocal', 'backing vocal', 'spare vocal'] },
  { keywords: ['singer', 'chanteur', 'chanteuse'], iconId: 'singer' },
  { keywords: ['condenser', 'condensateur'], iconId: 'condenser' },
  { keywords: ['ribbon', 'ruban'], iconId: 'ribbon' },
  { keywords: ['in-ear', 'iem', 'intra'], iconId: 'iem' },
  { keywords: ['headphones', 'casque'], iconId: 'headphones' },
  // Horns / brass
  { keywords: ['trombone'], iconId: 'trombone', exactNames: ['trombone'] },
  { keywords: ['trumpet', 'trompette'], iconId: 'trumpet', exactNames: ['trumpet'] },
  { keywords: ['saxophone', 'sax'], iconId: 'sax' },
  { keywords: ['french horn', 'cor'], iconId: 'frenchhorn' },
  { keywords: ['flute', 'flûte'], iconId: 'flute' },
  { keywords: ['clarinet', 'clarinette'], iconId: 'clarinet' },
  // Strings
  { keywords: ['violin', 'violon'], iconId: 'violin' },
  { keywords: ['viola', 'alto'], iconId: 'viola' },
  { keywords: ['cello', 'violoncelle'], iconId: 'cello' },
  { keywords: ['harp', 'harpe'], iconId: 'harp' },
  // Tech / computers
  { keywords: ['laptop tracks', 'laptop', 'portable'], iconId: 'laptop', exactNames: ['laptop tracks'] },
  { keywords: ['desktop', 'ordinateur'], iconId: 'desktop' },
  { keywords: ['tablet', 'tablette'], iconId: 'tablet' },
  { keywords: ['audio interface', 'audio-interface', 'interface audio'], iconId: 'audio-interface' },
  // Stage / ambience
  { keywords: ['ambiances', 'ambiance', 'ambience'], iconId: 'condenser', exactNames: ['ambiances'] },
  { keywords: ['wedge', 'monitor wedge'], iconId: 'wedge' },
  { keywords: ['sidefill', 'side fill'], iconId: 'sidefill' },
  { keywords: ['monitor console', 'monitor desk', 'console moniteur'], iconId: 'monitor' },
  { keywords: ['subwoofer', 'sub'], iconId: 'sub' },
  { keywords: ['line array', 'line-array'], iconId: 'linearray' },
  { keywords: ['pa speaker', 'pa-speaker', 'speaker'], iconId: 'paspeaker' },
  { keywords: ['distro', 'power distro', 'distribution'], iconId: 'distro' },
  { keywords: ['generator', 'générateur'], iconId: 'generator' },
  { keywords: ['truss'], iconId: 'truss' },
  { keywords: ['rack', 'equipment rack'], iconId: 'rack' },
  { keywords: ['flight case', 'flightcase'], iconId: 'flightcase' },
  { keywords: ['light fixture', 'light-fixture', 'par can', 'moving head', 'movinghead'], iconId: 'light-fixture' },
  { keywords: ['mic stand', 'mic-stand', 'pied de micro'], iconId: 'mic-stand-straight' },
  { keywords: ['dibox', 'di box', 'di-box'], iconId: 'dibox' },
  { keywords: ['cable', 'câble'], iconId: 'cable' },
  { keywords: ['chair', 'chaise'], iconId: 'chair' },
];

/**
 * Crew roles that DO occupy stage space (get a console / element on the plan).
 * `mon`/`foh` get a console element; `stagehand` is on stage but has no
 * element (reported in the report instead). Everything else is off-stage.
 */
const ROLE_RULES: Record<string, { iconId: string | null; category: PaletteCategory | null; onStage: boolean }> = {
  mon: { iconId: 'monitor', category: 'stage', onStage: true },
  'monitor engineer': { iconId: 'monitor', category: 'stage', onStage: true },
  foh: { iconId: 'monitor', category: 'stage', onStage: true },
  'front of house engineer': { iconId: 'monitor', category: 'stage', onStage: true },
  stagehand: { iconId: null, category: null, onStage: true },
  lights: { iconId: 'light-fixture', category: 'stage', onStage: true },
  'guitar tech': { iconId: null, category: null, onStage: true },
  'drum tech': { iconId: null, category: null, onStage: true },
  'keys tech': { iconId: null, category: null, onStage: true },
  crew: { iconId: null, category: null, onStage: true },
  driver: { iconId: null, category: null, onStage: false },
  'tour manager': { iconId: null, category: null, onStage: false },
  manager: { iconId: null, category: null, onStage: false },
  video: { iconId: null, category: null, onStage: false },
  other: { iconId: null, category: null, onStage: false },
  guest: { iconId: null, category: null, onStage: false },
};

/** Normalize a name for matching: lowercase, trim, collapse spaces. */
function normalize(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Map an instrument name to a palette icon.
 *
 * Strategy:
 *  1. exact (whole-string, case-insensitive) match against a rule's
 *     `exactNames` → confidence 1, matchedBy 'exact';
 *  2. otherwise the first rule whose keyword is a SUBSTRING of the name →
 *     confidence 0.8, matchedBy 'keyword';
 *  3. otherwise null, confidence 0, matchedBy 'none'.
 */
export function iconForInstrumentName(name: string): InstrumentIconMatch {
  const n = normalize(name);
  if (!n) return { iconId: null, confidence: 0, matchedBy: 'none' };

  for (const rule of INSTRUMENT_RULES) {
    if (rule.exactNames && rule.exactNames.some((e) => normalize(e) === n)) {
      return { iconId: rule.iconId, confidence: 1, matchedBy: 'exact' };
    }
  }
  for (const rule of INSTRUMENT_RULES) {
    for (const kw of rule.keywords) {
      if (n.includes(normalize(kw))) {
        return { iconId: rule.iconId, confidence: 0.8, matchedBy: 'keyword' };
      }
    }
  }
  return { iconId: null, confidence: 0, matchedBy: 'none' };
}

/** The companion amp icon for an instrument (e.g. e-guitar → guitar-amp). */
export function ampIconForInstrumentName(name: string): string | null {
  const n = normalize(name);
  if (!n) return null;
  for (const rule of INSTRUMENT_RULES) {
    if (rule.exactNames && rule.exactNames.some((e) => normalize(e) === n)) {
      return rule.ampIconId ?? null;
    }
  }
  for (const rule of INSTRUMENT_RULES) {
    for (const kw of rule.keywords) {
      if (n.includes(normalize(kw))) return rule.ampIconId ?? null;
    }
  }
  return null;
}

/**
 * Map a crew role to a palette icon + whether it occupies stage space.
 * Roles are matched case-insensitively against the closed CrewRole vocabulary
 * (plus the free-text aliases seen in real data, e.g. "Front of House
 * Engineer", "Monitor Engineer", "Tour Manager").
 */
export function iconForRole(role: string): RoleIconMatch {
  const n = normalize(role);
  if (!n) return { iconId: null, category: null, onStage: false };

  // Direct hit on the canonical role key.
  if (ROLE_RULES[n]) return ROLE_RULES[n];

  // Alias resolution: match the role text against known role keywords.
  const aliasMap: Record<string, keyof typeof ROLE_RULES> = {
    'monitor engineer': 'mon',
    'front of house engineer': 'foh',
    'tour manager': 'tour manager',
    'sound': 'foh',
    'monitor': 'mon',
    'lighting': 'lights',
    'light': 'lights',
    'ld': 'lights',
    'tm': 'tour manager',
    'stage': 'stagehand',
  };
  for (const [alias, key] of Object.entries(aliasMap)) {
    if (n.includes(alias)) return ROLE_RULES[key];
  }

  return { iconId: null, category: null, onStage: false };
}
